import {Text} from '@/components-v2/atoms'
import {LabelValue} from '@/components-v2/molecules'
import {PromotionStep} from '@/types/promotion'
import {PromotionType} from '@/utils/constants/promotionConstants'
import {PromotionReviewSection} from './PromotionReviewSection'

/**
 * @param {{
 *  promotionReviewData: import('@/types/promotion').Promotion,
 *  showEdit: boolean,
 *  setCurrentStep: (step: import('@/types/promotion').PromotionStep) => void,
 * }} props
 */
export const PromotionReviewSetup = ({promotionReviewData, showEdit, setCurrentStep}) => {
  const {segmentConfiguration, allCustomers} = promotionReviewData || {}

  const getExcludedItemsDisplay = () => {
    const excludedItems = promotionReviewData?.excludedItems
    if (!Array.isArray(excludedItems) || excludedItems.length === 0) return '-'
    const excludedItem = excludedItems?.[0] || {items: [], itemType: ''}
    if (!excludedItem?.items || excludedItem.items.length === 0) return '-'
    return excludedItem.items.map(item => item.name || item.id).join(', ')
  }

  const getProductCombination = () => {
    const firstCondition = promotionReviewData?.promotionConditions?.[0]?.conditionGroup?.conditions?.[0]
    return firstCondition?.productCombination ? 'Yes' : 'No'
  }

  const getExcludedSegmentsDisplay = () => {
    const excludedSegments = promotionReviewData?.segmentConfiguration?.excludedSegment?.segments
    if (excludedSegments?.length > 0) {
      return excludedSegments.map(seg => seg.segmentName || seg.segmentId).join(', ')
    }
    return '-'
  }

  const renderSegmentContent = () => {
    if (allCustomers) {
      return (
        <div className="grid grid-cols-3 gap-x-4">
          <LabelValue keyName="All Customers" val="Yes" />
          <LabelValue keyName="Excluded Segments" val={getExcludedSegmentsDisplay()} />
        </div>
      )
    }

    const includedSegments = segmentConfiguration?.includedSegments || []

    return (
      <div className="flex w-full flex-col gap-2 bg-[var(--color-background-layer-01)]">
        <div className="grid grid-cols-3 gap-x-4">
          <LabelValue keyName="All Customers" val="No" />
        </div>
        {includedSegments.length > 0 && (
          <div className="mt-2 grid grid-cols-3 gap-x-4">
            {includedSegments.map((group, idx) => {
              const segmentNames = (group?.segments || []).map(seg => seg.segmentName || seg.segmentId)
              const display = segmentNames.length > 0 ? segmentNames.join(', ') : '-'
              return (
                <div key={idx} className="flex flex-col gap-1">
                  <Text size="xsmall" variant="regular" textColorClass="text-gray-300">
                    Group {idx + 1}
                  </Text>
                  <Text size="small" variant="bold">
                    {display}
                  </Text>
                </div>
              )
            })}
          </div>
        )}
        <div className="grid grid-cols-3 gap-x-4">
          <LabelValue keyName="Excluded Segments" val={getExcludedSegmentsDisplay()} />
        </div>
      </div>
    )
  }
  const isOnlineVisible =
    promotionReviewData?.promotionType !== PromotionType.STORE_DISCOUNT &&
    promotionReviewData?.promotionType !== PromotionType.WELCOME_DISCOUNT

  return (
    <PromotionReviewSection label="Setup" showEdit={showEdit} onEdit={() => setCurrentStep(PromotionStep.SETUP)}>
      <div className="flex w-full flex-col gap-6 overflow-hidden">
        {/* Promotion */}
        <div>
          <Text size="small" variant="bold">
            {promotionReviewData?.promotionType || '-'}
          </Text>
          <div className="mt-2 h-px bg-[var(--secondary-800)]" />
          <div className="grid grid-cols-3 gap-x-4">
            <LabelValue keyName="Name" val={promotionReviewData?.name || '-'} />
            {isOnlineVisible && (
              <LabelValue keyName="Online Discount" val={promotionReviewData?.onlineDiscountEligible ? 'Yes' : 'No'} />
            )}
          </div>
          <div className="grid grid-cols-3 gap-x-4">
            <LabelValue keyName="Promotion" val={promotionReviewData?.promotionName || '-'} />
            <LabelValue keyName="Promotion Excludes" val={getExcludedItemsDisplay()} />
            <LabelValue keyName="Combination of Products" val={getProductCombination()} />
          </div>
        </div>

        {/* Segments */}
        <div>
          <Text size="small" variant="bold">
            Segments
          </Text>
          <div className="mt-2 h-px bg-[var(--secondary-800)]" />
          {renderSegmentContent()}
        </div>
      </div>
    </PromotionReviewSection>
  )
}
